<template>
  <aside class="panel">
    <!-- Nothing selected -->
    <template v-if="!cells.length">
      <div class="text-subtitle-1 font-weight-bold mb-1">
        {{ title }}
      </div>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Com <strong>Selecionar (V)</strong>, clique numa cadeira para editar nome, rótulo, tipo e coluna, ou num
        quadrado vazio para criar uma cadeira ali. Shift+clique seleciona um retângulo, Ctrl/⌘+clique alterna, e
        arrastar desenha um retângulo. Com várias selecionadas dá para mudar o tipo, mover, numerar ou remover de
        uma vez.
      </p>

      <div class="text-overline">
        Tipos nesta seção
      </div>
      <div
        v-for="type in seatTypes"
        :key="type"
        class="panel__legend"
      >
        <span class="panel__swatch" :style="{ borderColor: SeatTypeColors[type], background: `${SeatTypeColors[type]}1f` }">
          <v-icon
            v-if="SeatTypeIcons[type]"
            :icon="SeatTypeIcons[type]!"
            :color="SeatTypeColors[type]"
            size="9"
          />
        </span>
        {{ SeatTypeLabels[type] }}
        <span class="ml-auto text-medium-emphasis">{{ typeCounts[type] }}</span>
      </div>

      <v-divider class="my-4" />

      <div class="text-overline">
        Filas
      </div>
      <v-btn
        block
        variant="tonal"
        prepend-icon="fa-solid fa-arrow-up"
        class="mb-2 text-none"
        @click="addRow(0)"
      >
        Adicionar fila na frente
      </v-btn>
      <v-btn
        block
        variant="tonal"
        prepend-icon="fa-solid fa-arrow-down"
        class="text-none"
        @click="addRow(editor.rows.length)"
      >
        Adicionar fila no fundo
      </v-btn>
    </template>

    <!-- One empty cell -->
    <template v-else-if="singleEmpty">
      <div class="text-subtitle-1 font-weight-bold mb-3">
        Nova cadeira na fila {{ singleEmpty.row.symbol }}, coluna {{ singleEmpty.position }}
      </div>
      <v-form @submit.prevent="submitNewSeat">
        <v-text-field
          ref="newSeatName"
          v-model="newSeat.name"
          label="Nome"
          hint="Único na fila. Ex.: 19"
          persistent-hint
          variant="outlined"
          density="compact"
          class="mb-3"
        />
        <v-text-field
          v-model="newSeat.label"
          label="Rótulo (opcional)"
          :hint="`No ingresso: ${fullLabel(singleEmpty.row, newSeat)}`"
          persistent-hint
          variant="outlined"
          density="compact"
          class="mb-3"
        />
        <v-select
          v-model="newSeat.type"
          :items="typeItems"
          label="Tipo"
          variant="outlined"
          density="compact"
          hide-details
          class="mb-4"
        />
        <v-btn
          type="submit"
          color="primary"
          block
          prepend-icon="fa-solid fa-plus"
          class="text-none"
        >
          Adicionar
        </v-btn>
      </v-form>
    </template>

    <!-- One seat -->
    <template v-else-if="singleSeat">
      <div class="d-flex align-center mb-1">
        <div class="text-h5 font-weight-bold">
          {{ fullLabel(singleSeat.row, singleSeat.seat) }}
        </div>
        <v-spacer />
        <v-chip
          v-if="isNew(singleSeat.seat.id)"
          size="small"
          color="success"
        >
          Nova
        </v-chip>
      </div>
      <div class="text-body-2 text-medium-emphasis mb-4">
        Fila {{ singleSeat.row.symbol }} · coluna {{ singleSeat.seat.position }} · {{ SeatTypeLabels[singleSeat.seat.type] }}
      </div>

      <v-text-field
        ref="seatName"
        v-model="seatForm.name"
        label="Nome"
        hint="Único na fila. O CSV identifica a cadeira por fila + nome."
        persistent-hint
        variant="outlined"
        density="compact"
        @blur="applyName"
        @keydown.enter="applyName"
      />

      <v-text-field
        v-model="seatForm.label"
        label="Rótulo (opcional)"
        hint="Substitui o nome no ingresso. Ex.: 05 – MR"
        persistent-hint
        variant="outlined"
        density="compact"
        class="mt-4"
        @blur="applyLabel"
        @keydown.enter="applyLabel"
      />

      <div class="panel__ticket mt-3">
        No ingresso: <strong>{{ fullLabel(singleSeat.row, seatForm) }}</strong>
      </div>

      <div class="text-caption text-medium-emphasis mt-3 mb-1">
        Tipo: {{ SeatTypeLabels[singleSeat.seat.type] }}
      </div>
      <v-btn-toggle
        :model-value="singleSeat.seat.type"
        mandatory
        divided
        variant="outlined"
        density="comfortable"
        class="panel__types"
        @update:model-value="applyType"
      >
        <v-tooltip
          v-for="type in seatTypes"
          :key="type"
          :text="SeatTypeLabels[type]"
          location="top"
        >
          <template #activator="{ props: tip }">
            <v-btn
              v-bind="tip"
              :value="type"
              :color="SeatTypeColors[type]"
            >
              <v-icon :icon="SeatTypeIcons[type] ?? 'fa-solid fa-chair'" size="16" />
            </v-btn>
          </template>
        </v-tooltip>
      </v-btn-toggle>

      <v-text-field
        v-model.number="seatForm.position"
        type="number"
        label="Coluna (posição)"
        hint="Contada da esquerda, palco em cima. Se houver outra cadeira lá, as duas trocam de lugar."
        persistent-hint
        :min="1"
        variant="outlined"
        density="compact"
        class="mt-4"
        @blur="applyPosition"
        @keydown.enter="applyPosition"
      />

      <v-btn
        block
        color="error"
        variant="tonal"
        prepend-icon="fa-solid fa-trash"
        class="mt-6 text-none"
        @click="removeSelected"
      >
        Remover cadeira
      </v-btn>
    </template>

    <!-- Several cells -->
    <template v-else>
      <div class="text-h6 font-weight-bold">
        {{ plural(seats.length, 'cadeira selecionada', 'cadeiras selecionadas') }}
      </div>
      <div class="text-body-2 text-medium-emphasis mb-1">
        em {{ plural(rowCount, 'fila', 'filas') }}
      </div>
      <div class="text-caption text-medium-emphasis mb-3">
        {{ selectionSummary }}
      </div>

      <template v-if="seats.length">
        <div class="text-overline">
          Tipo de todas
        </div>
        <div class="panel__bulk-types">
          <v-btn
            v-for="type in seatTypes"
            :key="type"
            size="small"
            variant="outlined"
            class="text-none panel__bulk"
            :color="SeatTypeColors[type]"
            :prepend-icon="SeatTypeIcons[type] ?? 'fa-solid fa-chair'"
            @click="bulkType(type)"
          >
            {{ SeatTypeLabels[type] }}
            <template #append>
              <span class="panel__count">{{ seats.filter(c => c.seat.type === type).length }}</span>
            </template>
          </v-btn>
        </div>

        <div class="text-overline mt-3">
          Mover na própria fila
        </div>
        <div class="d-flex ga-2">
          <v-btn
            size="small"
            variant="tonal"
            class="text-none flex-grow-1"
            @click="emit('shift', -1)"
          >
            ← 1 coluna
          </v-btn>
          <v-btn
            size="small"
            variant="tonal"
            class="text-none flex-grow-1"
            @click="emit('shift', 1)"
          >
            1 coluna →
          </v-btn>
        </div>
        <div class="text-caption text-medium-emphasis mt-1">
          Atalho: Shift+← / Shift+→
        </div>

        <div class="text-overline mt-3">
          Numerar
        </div>
        <template v-if="renumberRow">
          <v-select
            v-model="bulkNumber.pattern"
            :items="patternItems"
            label="Padrão"
            variant="outlined"
            density="compact"
            hide-details
            class="mb-2"
          />
          <div class="d-flex align-center ga-2 mb-2">
            <v-text-field
              v-model.number="bulkNumber.start"
              type="number"
              label="Começa em"
              variant="outlined"
              density="compact"
              hide-details
              style="flex: 0 0 110px"
            />
            <v-checkbox
              v-model="bulkNumber.twoDigits"
              label="Dois dígitos (01)"
              density="compact"
              hide-details
            />
          </div>
          <div class="panel__preview mb-2">
            <span
              v-for="(name, index) in renumberPreview"
              :key="index"
              class="panel__preview-cell"
            >{{ name }}</span>
          </div>
          <v-btn
            block
            size="small"
            variant="tonal"
            color="primary"
            class="text-none"
            prepend-icon="fa-solid fa-arrow-down-1-9"
            @click="applyBulkNumbering"
          >
            Numerar {{ plural(seats.length, 'cadeira', 'cadeiras') }} da fila {{ renumberRow.symbol }}
          </v-btn>
        </template>
        <div v-else class="text-caption text-medium-emphasis">
          Para numerar, selecione cadeiras de uma fila só.
        </div>

        <v-btn
          block
          color="error"
          variant="tonal"
          prepend-icon="fa-solid fa-trash"
          class="mt-5 text-none"
          @click="removeSelected"
        >
          Remover {{ plural(seats.length, 'cadeira', 'cadeiras') }}
        </v-btn>
      </template>
      <v-btn
        block
        variant="text"
        class="mt-2 text-none"
        @click="emit('clear')"
      >
        Limpar seleção (Esc)
      </v-btn>
    </template>

    <v-divider class="my-4" />

    <button
      type="button"
      class="history__toggle"
      @click="historyOpen = !historyOpen"
    >
      <v-icon :icon="historyOpen ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" size="10" />
      Histórico ({{ editor.history.length }})
    </button>
    <template v-if="historyOpen">
      <div v-if="!editor.history.length" class="text-caption text-medium-emphasis mt-1">
        Nenhuma alteração para desfazer.
      </div>
      <ol v-else class="history">
        <li
          v-for="(entry, index) in recentHistory"
          :key="editor.history.length - index"
          class="history__item"
          :class="{ 'history__item--next': index === 0 }"
          :title="entry.label"
        >
          {{ entry.label }}
        </li>
      </ol>
      <div v-if="editor.history.length > HISTORY_VISIBLE" class="text-caption text-medium-emphasis">
        e mais {{ plural(editor.history.length - HISTORY_VISIBLE, 'passo anterior', 'passos anteriores') }}
      </div>
    </template>
  </aside>
</template>

<script lang="ts" setup>
import { computed, nextTick, reactive, ref, useTemplateRef, watch, type Ref } from 'vue'
import {
  fullLabel,
  isNew,
  numberSeries,
  plural,
  rowDigits,
  type GridCell,
  type HistoryEntry,
  type NumberingPattern,
  type SeatCell,
  type SeatEditor,
} from '@/helpers/seatEditor/editor'
import { SeatType, SeatTypeColors, SeatTypeIcons, SeatTypeLabels } from '@/constants/SeatTypes'
import type { GridRow } from '@/types/SectionGrid'
import { PATTERN_ITEMS } from './tools'

interface Props {
  editor: SeatEditor
  /** The selected cells, in the order they were selected */
  cells: GridCell[]
  /** "Setor › Seção" */
  title: string
  /** Digits for a new seat in an empty Row (the "Dois dígitos" option) */
  digits: number
}

const { editor, cells, title, digits } = defineProps<Props>()

const emit = defineEmits<{
  /** Selects one cell */
  select: [rowId: string, position: number]
  clear: []
  /** Moves the selected seats within their Rows */
  shift: [delta: number]
  /** A change was made, described for "Última ação" */
  done: [text: string]
  fail: [message: string]
}>()

defineOptions({ name: 'SeatEditorPanel' })

const HISTORY_VISIBLE = 10

const seatTypes: SeatType[] = Object.values(SeatType)
const typeItems = seatTypes.map(type => ({ title: SeatTypeLabels[type], value: type }))
const patternItems = PATTERN_ITEMS

const seats = computed<SeatCell[]>(() => cells.filter((cell): cell is SeatCell => !!cell.seat))
const rowCount = computed<number>(() => new Set(seats.value.map(cell => cell.row.id)).size)
const singleEmpty = computed<GridCell | null>(() => (cells.length === 1 && !cells[0].seat ? cells[0] : null))
const singleSeat = computed<SeatCell | null>(() => (cells.length === 1 && seats.value.length === 1 ? seats.value[0] : null))

const typeCounts = computed<Record<SeatType, number>>(() => {
  const counts = Object.fromEntries(seatTypes.map(type => [type, 0])) as Record<SeatType, number>
  editor.rows.forEach(row => row.seats.forEach((seat) => {
    counts[seat.type]++
  }))
  return counts
})

const selectionSummary = computed<string>(() => {
  const labels = [ ...seats.value ]
    .sort((a, b) => a.rowIndex - b.rowIndex || a.position - b.position)
    .map(cell => fullLabel(cell.row, cell.seat))
  return labels.length > 12 ? `${labels.slice(0, 12).join(', ')} e mais ${labels.length - 12}` : labels.join(', ')
})

/** Reports a change's outcome: the error, or what was done */
function report(error: string | null, done: string) {
  if (error) emit('fail', error)
  else emit('done', done)
}

function addRow(index: number) {
  const error = editor.addRow(index)
  report(error, error ? '' : `criou a fila ${editor.rows[index].symbol} (vazia)`)
}

// Focus on demand ---------------------------------------------------------------------------------------------------

const seatNameField = useTemplateRef<{ focus: () => void, select: () => void }>('seatName')
const newSeatNameField = useTemplateRef<{ focus: () => void, select: () => void }>('newSeatName')

/** Puts the cursor on the Name field of the selected seat or empty cell */
function focusName() {
  nextTick(() => {
    const field = singleSeat.value ? seatNameField.value : newSeatNameField.value
    field?.focus()
    field?.select()
  })
}

defineExpose({ focusName, resetSeatForm })

// New seat ----------------------------------------------------------------------------------------------------------

const newSeat = reactive<{ name: string, label: string, type: SeatType }>({ name: '', label: '', type: SeatType.STANDARD })

watch(() => (singleEmpty.value ? `${singleEmpty.value.row.id}|${singleEmpty.value.position}` : null), (key) => {
  const cell = singleEmpty.value
  if (!key || !cell) return
  newSeat.name = editor.suggestName(cell.row.id, digits)
  newSeat.label = ''
  newSeat.type = SeatType.STANDARD
}, { immediate: true })

function submitNewSeat() {
  const cell = singleEmpty.value
  if (!cell) return

  const error = editor.addSeat(cell.row.id, cell.position, { name: newSeat.name, label: newSeat.label, type: newSeat.type })
  report(error, `adicionou ${fullLabel(cell.row, newSeat)} na coluna ${cell.position}`)
  if (!error) emit('select', cell.row.id, cell.position)
}

// One seat ----------------------------------------------------------------------------------------------------------

const seatForm = reactive<{ name: string, label: string, position: number }>({ name: '', label: '', position: 1 })

function resetSeatForm() {
  const cell = singleSeat.value
  if (!cell) return
  seatForm.name = cell.seat.name
  seatForm.label = cell.seat.label ?? ''
  seatForm.position = cell.seat.position
}

watch(
  () => singleSeat.value && `${singleSeat.value.seat.id}|${singleSeat.value.seat.name}|${singleSeat.value.seat.label}|${singleSeat.value.seat.position}`,
  resetSeatForm,
  { immediate: true },
)

function applyName() {
  const cell = singleSeat.value
  if (!cell || seatForm.name === cell.seat.name) return

  const before = fullLabel(cell.row, cell.seat)
  const error = editor.updateSeat(cell.seat.id, { name: seatForm.name })
  if (error) resetSeatForm()
  report(error, `renomeou ${before} → ${cell.row.symbol}${seatForm.name.trim()}`)
}

function applyLabel() {
  const cell = singleSeat.value
  if (!cell || seatForm.label.trim() === (cell.seat.label ?? '')) return

  report(editor.updateSeat(cell.seat.id, { label: seatForm.label }), `rótulo de ${cell.row.symbol}${cell.seat.name} → "${seatForm.label.trim()}"`)
}

function applyType(type: SeatType) {
  const cell = singleSeat.value
  if (!cell || cell.seat.type === type) return

  report(editor.updateSeat(cell.seat.id, { type }), `${fullLabel(cell.row, cell.seat)} → ${SeatTypeLabels[type]}`)
}

function applyPosition() {
  const cell = singleSeat.value
  const position = Number(seatForm.position)
  if (!cell || position === cell.seat.position) return

  const other = cell.row.seats.find(seat => seat.position === position)
  const label = fullLabel(cell.row, cell.seat)
  const error = editor.updateSeat(cell.seat.id, { position })
  if (error) resetSeatForm()
  report(error, `${label} → coluna ${position}${other ? ` (trocou com ${fullLabel(cell.row, other)})` : ''}`)
}

// Several seats -----------------------------------------------------------------------------------------------------

function bulkType(type: SeatType) {
  const ids = seats.value.filter(cell => cell.seat.type !== type).map(cell => cell.seat.id)
  if (!ids.length) return

  report(editor.setType(ids, type), `${plural(ids.length, 'cadeira', 'cadeiras')} → ${SeatTypeLabels[type]}`)
}

function removeSelected() {
  const ids = seats.value.map(cell => cell.seat.id)
  if (!ids.length) return

  report(editor.removeSeats(ids), `removeu ${plural(ids.length, 'cadeira', 'cadeiras')}`)
}

const renumberRow = computed<GridRow | null>(() => (seats.value.length > 1 && rowCount.value === 1 ? seats.value[0].row : null))

const bulkNumber = reactive<{ pattern: NumberingPattern, start: number, twoDigits: boolean }>({ pattern: 'ascending', start: 1, twoDigits: true })

watch(() => renumberRow.value?.id, () => {
  if (renumberRow.value) bulkNumber.twoDigits = rowDigits(renumberRow.value, 2) === 2
}, { immediate: true })

const renumberPreview = computed<string[]>(() => {
  const start = Number.isFinite(bulkNumber.start) ? Math.floor(bulkNumber.start) : 1
  return numberSeries(seats.value.length, bulkNumber.pattern, start, bulkNumber.twoDigits ? 2 : 1)
})

/** Renames the selected seats in place, left to right: each keeps its id, Label and Type */
function applyBulkNumbering() {
  const row = renumberRow.value
  if (!row) return

  const names = renumberPreview.value
  const ordered = [ ...seats.value ].sort((a, b) => a.position - b.position)
  const error = editor.renameSeats(ordered.map((cell, i) => ({ seatId: cell.seat.id, name: names[i] })))
  report(error, `fila ${row.symbol}: ${names[0]} … ${names[names.length - 1]}`)
}

// History -----------------------------------------------------------------------------------------------------------

const historyOpen: Ref<boolean> = ref(true)

const recentHistory = computed<HistoryEntry[]>(() => [ ...editor.history ].reverse().slice(0, HISTORY_VISIBLE))
</script>

<style scoped>
.panel {
  flex: 0 0 320px;
  width: 320px;
  align-self: flex-start;
  position: sticky;
  top: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background: #fafbfc;
}

.panel__legend {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  padding: 3px 0;
}

.panel__swatch {
  width: 22px;
  height: 22px;
  border: 1.5px solid;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel__ticket {
  padding: 6px 10px;
  border-radius: 4px;
  background: #eceff1;
  font-size: 13px;
}

.panel__types {
  width: 100%;
}

.panel__types :deep(.v-btn) {
  flex: 1;
}

.panel__bulk-types {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}

.panel__bulk {
  letter-spacing: normal;
  font-size: 12px;
}

.panel__bulk :deep(.v-btn__content) {
  flex: 1;
  justify-content: flex-start;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel__count {
  font: 600 11px/1 ui-monospace, Menlo, monospace;
  opacity: 0.7;
}

.panel__preview {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.panel__preview-cell {
  min-width: 26px;
  height: 22px;
  padding: 0 3px;
  border: 1.5px solid #fb8c00;
  border-radius: 4px;
  background: #fff3e0;
  font: 600 11px/19px ui-monospace, Menlo, monospace;
  text-align: center;
  color: #e65100;
}

.history__toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(0, 0, 0, 0.7);
}

.history {
  list-style: none;
  margin-top: 6px;
  font-size: 12px;
}

.history__item {
  padding: 2px 6px;
  border-left: 2px solid #e0e0e0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: rgba(0, 0, 0, 0.6);
}

.history__item--next {
  border-left-color: rgb(var(--v-theme-primary));
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
}
</style>
